import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { money, fmtDate } from "@/lib/constants";
import { useCamp } from "@/context/SettingsContext";
import PrintLogo from "@/components/print/PrintLogo";
import { Button } from "@/components/ui/button";
import DemoStamp from "@/components/DemoStamp";

function Line({ label, value, bold = false, muted = false }) {
  return (
    <div className={`flex justify-between py-1 border-b border-slate-200 text-sm ${bold ? "font-bold" : ""}`}>
      <span className={muted ? "text-slate-500" : ""}>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

export default function PrintCashClose() {
  const { t } = useLang();
  const { camp } = useCamp();
  const [params] = useSearchParams();
  const date = params.get("date");
  const [rep, setRep] = useState(null);

  useEffect(() => { api.get(`/cash/day?date=${date}`).then(({ data }) => setRep(data)).catch(() => {}); }, [date]);
  useEffect(() => {
    if (!rep) return;
    const timer = setTimeout(() => window.print(), 500);
    return () => clearTimeout(timer);
  }, [rep]);

  if (!rep) return <div className="p-8 text-sm text-muted-foreground">…</div>;
  const c = rep.closure || {};

  return (
    <div className="print-page bg-white text-slate-900 mx-auto max-w-[520px] p-8 print:p-0" data-testid="print-cashclose">
      <DemoStamp />
      <div className="flex justify-end print:hidden mb-3">
        <Button size="sm" onClick={() => window.print()} data-testid="print-cashclose-now"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
      </div>
      <div className="flex items-center gap-3">
        <PrintLogo camp={camp} testid="print-cashclose-logo" />
        <div>
          <h1 className="font-display font-extrabold text-xl">{camp.camp_name}</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("admin_cashclose")} · {rep.date}</p>
        </div>
      </div>

      <div className="mt-5">
        <Line label={t("sales")} value={rep.count} />
        <Line label={t("pay_cash")} value={money(rep.cash_total)} />
        <Line label={t("pay_account")} value={money(rep.account_total)} />
        <Line label={t("total")} value={money(rep.total)} bold />
        {rep.account_paid_cash ? <Line label={t("cash_account_paid")} value={money(rep.account_paid_cash)} /> : null}
        {rep.online_deposits ? <Line label={t("cash_online_deposits")} value={money(rep.online_deposits)} /> : null}
        <Line label={t("deposits_cash")} value={money((rep.deposits_in || 0) - (rep.deposits_out || 0))} muted />
        <Line label={t("cash_opening")} value={money(rep.opening_float || 0)} muted />
        <Line label={t("cash_expected")} value={money(rep.cash_expected)} bold />
        <Line label={t("cash_counted")} value={c.counted_cash == null ? "—" : money(c.counted_cash)} />
        <Line label={t("cash_difference")} value={c.difference == null ? "—" : money(c.difference)} />
        <Line label={t("cash_keep")} value={c.float_next == null ? "—" : money(c.float_next)} />
        <Line label={t("cash_to_deposit")} value={c.to_deposit == null ? "—" : money(c.to_deposit)} bold />
      </div>

      {(rep.vat_summary || []).length > 0 && (
        <table className="mt-5 w-full text-xs">
          <thead><tr className="text-left text-slate-500"><th>{t("vat")}</th><th className="text-right">{t("subtotal")}</th><th className="text-right">{t("vat")}</th></tr></thead>
          <tbody>
            {rep.vat_summary.map((v) => (
              <tr key={v.rate}><td>{v.rate}%</td><td className="text-right tabular-nums">{money(v.base)}</td><td className="text-right tabular-nums">{money(v.vat)}</td></tr>
            ))}
          </tbody>
        </table>
      )}

      {(c.counted_denoms || c.float_denoms) && (
        <div className="mt-5 grid grid-cols-2 gap-6 text-xs" data-testid="print-cashclose-denoms">
          {[[t("cash_counted"), c.counted_denoms], [t("cash_keep"), c.float_denoms]].map(([lbl, counts]) => (
            <div key={lbl}>
              <p className="font-semibold uppercase tracking-wider text-slate-500">{lbl}</p>
              {Object.entries(counts || {}).filter(([, q]) => Number(q) > 0)
                .sort((a, b) => Number(b[0]) - Number(a[0]))
                .map(([d, q]) => (
                  <p key={d} className="flex justify-between tabular-nums">
                    <span>{Number(d) >= 1 ? `€ ${d}` : `${Math.round(Number(d) * 100)}c`}</span>
                    <span>{q} × = {money(Number(d) * Number(q))}</span>
                  </p>
                ))}
            </div>
          ))}
        </div>
      )}

      {c.note && <p className="mt-4 text-xs text-slate-600">{c.note}</p>}
      <p className="mt-6 text-[11px] text-slate-500">
        {c.closed_at ? `${fmtDate(c.closed_at)} · ${c.closed_by || ""}` : t("cash_not_closed")}
      </p>
      <div className="mt-8 border-t border-slate-300 pt-6 text-[11px] text-slate-500">{t("cash_signature")}</div>
    </div>
  );
}
